'use client';

import { isInputSafe, sanitizeEmail, sanitizeInput, sanitizePhoneNumber } from '@/app/utils/sanitization';

/*
 * The contact step's checks: exactly the lead form's (components/form/TextPanel.tsx handleSubmit and
 * validateInput, panels full-name-reg, email-reg and phone-reg), with the same messages. Keep the two in step.
 */

export type ContactField = 'name' | 'email' | 'phone';

/** The lead form's panel for each field, for its "field invalid" analytics event. */
export const CONTACT_PANELS: Record<ContactField, { id: string; label: string }> = {
  name: { id: 'full-name-reg', label: 'Full Name*' },
  email: { id: 'email-reg', label: 'Email' },
  phone: { id: 'phone-reg', label: 'Phone*' },
};

const MAX_INPUT_LENGTH = 256;
const MIN_NAME_LENGTH = 2;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const NAME_REGEX = /^[A-Za-z]+(\s+[A-Za-z]+)*$/;

type PhoneNumberUtilInstance = ReturnType<(typeof import('google-libphonenumber'))['PhoneNumberUtil']['getInstance']>;
let phoneUtilPromise: Promise<PhoneNumberUtilInstance> | undefined;
const getPhoneUtil = (): Promise<PhoneNumberUtilInstance> => {
  phoneUtilPromise ??= import('google-libphonenumber').then(({ PhoneNumberUtil }) => PhoneNumberUtil.getInstance());
  return phoneUtilPromise;
};

/** A real US number, by Google's libphonenumber (formatted input like (XXX) XXX-XXXX is fine). */
const isValidUSPhone = async (raw: string): Promise<boolean> => {
  try {
    const phoneUtil = await getPhoneUtil();
    return phoneUtil.isValidNumberForRegion(phoneUtil.parseAndKeepRawInput(raw, 'US'), 'US');
  } catch {
    return false;
  }
};

/** The phone field as the visitor types: digits only, a leading 1 dropped, capped at 10, shown as (XXX) XXX-XXXX. */
export const formatPhoneInput = (input: string): string => {
  let digits = input.replace(/\D/g, '');
  if (digits.length > 10 && digits.startsWith('1')) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export type FieldResult = { value: string; error: string | null };

/** Sanitizes then validates one field, the way the lead form's text panel does. `value` is what the lead gets. */
export const checkContactField = async (field: ContactField, raw: string): Promise<FieldResult> => {
  const value = field === 'email' ? sanitizeEmail(raw) : field === 'phone' ? sanitizePhoneNumber(raw) : sanitizeInput(raw);

  // A sanitizer that empties a non-blank entry has rejected it.
  if (!value && raw.trim()) {
    return { value: '', error: field === 'email' ? 'Invalid Email' : field === 'phone' ? 'Invalid Phone' : `Please enter a valid ${CONTACT_PANELS[field].label.toLowerCase()}` };
  }

  if (!value.trim()) {
    // Email may be left blank on the lead form; name and phone are required.
    if (field === 'email') return { value: '', error: null };
    return { value: '', error: field === 'phone' ? 'Phone number is required' : 'This field is required' };
  }
  if (value.length > MAX_INPUT_LENGTH) return { value, error: `Input must be less than ${MAX_INPUT_LENGTH} characters` };

  if (field === 'name') {
    if (value.trim().length < MIN_NAME_LENGTH) return { value, error: `Name must be at least ${MIN_NAME_LENGTH} characters` };
    if (!NAME_REGEX.test(value.trim())) return { value, error: 'Please enter a valid name' };
  }
  if (field === 'email' && !EMAIL_REGEX.test(value)) return { value, error: 'Invalid Email' };
  if (field === 'phone' && !(await isValidUSPhone(value))) return { value, error: 'Invalid Phone' };
  if (!isInputSafe(value)) return { value, error: 'Input contains invalid characters' };

  return { value, error: null };
};
